import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ApiService } from 'src/app/services/api.service';
import { PopupService } from 'src/app/services/popup.service';
import { minActionCredits, normalizeCreditActions, VesselFinderCreditAction } from 'src/app/services/operators/vesselfinder-credit-actions';
import { CollectionPopupComponent } from '../../../../../shared/index';
import {
  canRequestVesselFinderRefund,
  canSubmitVesselFinderRefund,
  hasVesselFinderToiFinished,
  vesselFinderCreditsRemaining,
} from '../../vf-credits.model';

/**
 * VesselFinder credit usage + refund, in one dialog — a deliberate duplicate
 * of `VoyageCreditRefundComponent` (same reasoning as the purchase/info
 * dialogs above it in this operator's file tree). Everything is derived from
 * the wallet pair already on the sensor (`apiCredits`/`apiCreditsRemaining`);
 * no request is made to render it.
 *
 * Submits through the SAME generic `/tasks/returnRefundRequest` endpoint
 * Voyage Radar uses, keyed by `product: 'VesselFinder'` — mirrored server-side
 * in `tasks.route.js` beside the `voyageradar` branch.
 *
 * Not yet wired to an open() trigger anywhere — the "refund" icon that opens
 * `VoyageCreditRefundComponent` for Voyage Radar lives inside
 * `leaflet-newcollections.component.ts`, the same large map component the
 * marker-placement facade still needs wiring into. Deferred to that same
 * follow-up rather than edited piecemeal ahead of it.
 */
@Component({
  selector: 'app-vesselfinder-credit-refund',
  templateUrl: './vesselfinder-credit-refund.component.html',
  styleUrls: ['../../../voyage-radar/panels/credit-refund/voyage-credit-refund.component.scss'],
})
export class VesselFinderCreditRefundComponent implements OnInit {
  formGroup: FormGroup;
  private reasonControl: FormControl;

  readonly minMsgLength = 5;
  readonly maxMsgLength = 500;
  submitting = false;

  constructor(
    private dialogRef: MatDialogRef<VesselFinderCreditRefundComponent>,
    private apiService: ApiService,
    private popupService: PopupService,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {}

  ngOnInit(): void {
    this.reasonControl = new FormControl(
      'I would like to request the remainder of the credits to be refunded.',
      [Validators.required, Validators.minLength(this.minMsgLength), Validators.maxLength(this.maxMsgLength)],
    );
    this.formGroup = new FormGroup({ reason: this.reasonControl });
  }

  get sensor(): any {
    return this.data?.sensor;
  }

  get reason(): FormControl {
    return this.reasonControl;
  }

  get reasonLength(): number {
    return `${this.reasonControl?.value ?? ''}`.length;
  }

  /** No AOI/POI narrowing for this operator in this phase — the whole catalogue applies. */
  get actions(): VesselFinderCreditAction[] {
    return normalizeCreditActions(this.sensor?.apiCreditActions);
  }

  get purchased(): number {
    const n = Number(this.sensor?.apiCredits);
    return Number.isFinite(n) ? Math.max(0, n) : 0;
  }

  get remaining(): number | null {
    return vesselFinderCreditsRemaining(this.sensor);
  }

  get used(): number {
    const left = this.remaining;
    return left == null ? 0 : Math.max(0, this.purchased - left);
  }

  get usedPct(): number {
    return this.purchased ? Math.min(100, (this.used * 100) / this.purchased) : 0;
  }

  get unitCost(): number {
    return Number(this.sensor?.unitCost) || 0;
  }

  get showMoney(): boolean {
    return this.unitCost > 0;
  }

  get estimatedRefund(): number {
    return (this.remaining ?? 0) * this.unitCost;
  }

  get stranded(): boolean {
    const left = this.remaining;
    return left != null && left > 0 && left < minActionCredits(this.actions);
  }

  get allUsed(): boolean {
    return this.remaining === 0;
  }

  get refundApproved(): boolean {
    return !!this.sensor?.returnRefundAccept;
  }

  get refundedCredits(): number {
    const n = Number(this.sensor?.refundedApiCredits);
    return Number.isFinite(n) ? Math.max(0, n) : 0;
  }

  get refundedValue(): number {
    return this.refundedCredits * this.unitCost;
  }

  get refundDeclined(): boolean {
    return !!this.sensor?.returnRefundReject;
  }

  get refundPending(): boolean {
    return !!this.sensor?.refundRequest && !this.refundApproved && !this.refundDeclined;
  }

  get toiFinished(): boolean {
    return hasVesselFinderToiFinished(this.sensor);
  }

  get canRequestRefund(): boolean {
    return canRequestVesselFinderRefund(this.sensor);
  }

  get canSubmitRefund(): boolean {
    return canSubmitVesselFinderRefund(this.sensor);
  }

  get errorMessage(): string {
    const c = this.reasonControl;
    if (!c || c.valid) return '';
    if (c.hasError('required')) return 'Cannot be empty';
    if (c.hasError('minlength')) return `Must be at least ${this.minMsgLength} characters`;
    if (c.hasError('maxlength')) return `Must be no more than ${this.maxMsgLength} characters`;
    return 'Must be valid';
  }

  onCancel(): void {
    this.dialogRef.close('close');
  }

  onSubmit(): void {
    if (!this.canSubmitRefund || this.formGroup.invalid || this.submitting) return;
    this.submitting = true;

    this.apiService
      .returnRefundRequest({
        taskId: this.data?.taskId,
        sensorId: this.data?.sensorId,
        sceneId: undefined,
        refundRequest: true,
        refundRequestReason: this.reasonControl.value,
        product: 'VesselFinder',
        action: 'vesselfinder-credits',
        feedId: undefined,
      })
      .subscribe(
        (res: any) => {
          if (res?.msg === 'Success') {
            this.dialogRef.close({ refundRequested: true });
            return;
          }
          this.submitting = false;
          this.popupService.openPopup(
            CollectionPopupComponent,
            'An error occurred during processing and ingestion of the request. Please try after sometime.',
            '',
            '40%',
          );
        },
        () => {
          this.submitting = false;
          this.popupService.openPopup(
            CollectionPopupComponent,
            'An error occurred during processing and ingestion of the request. Please try after sometime.',
            '',
            '40%',
          );
        },
      );
  }
}
